import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { postReview } from "../api/review";

const ReviewForm = ({ product, user, token, fetchAndSetReviews }) => {
  const [newReviewRating, setNewReviewRating] = useState(0);
  const [newReviewText, setNewReviewText] = useState("");
  const [reviewFormError, setReviewFormError] = useState(null);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setReviewFormError(null);

    if (!user || !token) {
      toast.error("You must be logged in to post a review.");
      navigate("/login");
      return;
    }
    if (!newReviewText.trim()) {
      setReviewFormError("Review comment cannot be empty.");
      return;
    }
    if (newReviewRating === 0) {
      setReviewFormError("Please select a rating.");
      return;
    }

    setIsReviewSubmitting(true);
    try {
      const response = await postReview(
        product._id,
        user._id,
        newReviewRating,
        newReviewText,
        user.name,
        user.email,
        token
      );

      if (response.message === "Review submitted") {
        toast.success(response.message);
        setNewReviewText("");
        setNewReviewRating(0);
        await fetchAndSetReviews(product._id);
      } else {
        toast.error(response.message);
        setReviewFormError(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while submitting your review.");
      setReviewFormError("An error occurred.");
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  if (!user) {
    return (
      <p className="text-lg text-gray-400">
        Please{" "}
        <span
          className="text-cyan-400 hover:underline cursor-pointer"
          onClick={() => navigate("/login")}
        >
          log in
        </span>{" "}
        to write a review.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmitReview} className="space-y-6 animate-fadeIn">
      <div>
        <label className="block text-xl font-semibold text-purple-300 mb-3">
          Your Rating:
        </label>
        <div className="flex space-x-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              size={30}
              fill={newReviewRating >= star ? "#FACC15" : "none"}
              stroke={newReviewRating >= star ? "#FACC15" : "currentColor"}
              className={`cursor-pointer transition-all duration-200 ${
                newReviewRating >= star
                  ? "text-yellow-400 drop-shadow-glow"
                  : "text-gray-500 hover:text-yellow-300"
              }`}
              onClick={() => setNewReviewRating(star)}
            />
          ))}
        </div>
        {reviewFormError?.includes("rating") && (
          <p className="text-pink-400 text-sm mt-1">{reviewFormError}</p>
        )}
      </div>

      <div>
        <label className="block text-xl font-semibold text-purple-300 mb-3">
          Your Comment:
        </label>
        <textarea
          className="w-full p-4 rounded-2xl bg-gradient-to-r from-gray-900 to-gray-800 text-white border border-purple-700 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 min-h-[120px] placeholder-gray-400 transition-all duration-300"
          value={newReviewText}
          onChange={(e) => setNewReviewText(e.target.value)}
          placeholder="Share your thoughts..."
          required
        />
        {reviewFormError?.includes("comment") && (
          <p className="text-pink-400 text-sm mt-1">{reviewFormError}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isReviewSubmitting}
        className={`px-6 py-3 rounded-2xl text-lg font-bold tracking-wide flex items-center gap-3 shadow-lg transition-all duration-300 ${
          isReviewSubmitting
            ? "bg-gray-700 text-gray-300 cursor-not-allowed"
            : "bg-gradient-to-r from-teal-500 to-green-600 hover:from-green-600 hover:to-teal-500 text-white shadow-green-500/30 hover:shadow-lg"
        }`}
      >
        {isReviewSubmitting ? (
          <>
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Submitting...
          </>
        ) : (
          "Submit Review"
        )}
      </button>

      {reviewFormError && !reviewFormError.includes("rating") && !reviewFormError.includes("comment") && (
        <p className="text-pink-400 text-sm mt-2">{reviewFormError}</p>
      )}
    </form>
  );
};

export default ReviewForm;
